import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import {
  Alert,
  AlertDescription,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { LayoutGridIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { TagInput } from '@/shared/ui/TagInput';
import type { ToolRowData } from '../ToolsTable/ToolsTable.types';
import type { WorkspaceAccess } from '../../model/tools.types';
import { ToolBadge } from '@/shared/ui/ToolBadge';
import type { ManageToolWorkspacesSheetProps } from './ManageToolWorkspacesSheet.types';

const ACCESS_FIELD_ID = 'tool-workspace-access';
const WORKSPACE_PICKER_ID = 'tool-workspace-picker';

/**
 * Tessra replacement for `ManageToolWorkspacesModal`, plus the failure surface the
 * MUI original never had: a rejected save used to leave the surface open and silent,
 * so an admin walked away believing permissions had changed.
 *
 * An edge panel rather than a centre modal, because this modifies what a tool can
 * reach. Sizing follows the two sheets already in this slice, `AddWhitelistRuleModal`
 * and `McpCatalogFormSheet`.
 *
 * The staged selection lives in `ManageToolWorkspacesForm`, which renders inside
 * `SheetContent`. Radix removes that subtree when `open` goes false, so reopening
 * mounts a fresh instance and the mode and chips are re-seeded from the row — the
 * reset the MUI version needed an effect for, and the reason `useModalState`'s
 * `instanceKey` is not required here. `SheetContent` has no `onCloseComplete` twin
 * either, so the host's own unmount timer is what ends the exit.
 */
export function ManageToolWorkspacesSheet(props: ManageToolWorkspacesSheetProps) {
  const { open, onClose, tool, allWorkspaces, onSave, isSaving, isError } = props;

  if (!tool) return null;

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <SheetContent
        // The shared shells' layout, kept here because the form below must own the header and the
        // footer to remount with the content: no horizontal padding on the panel, so the body can
        // scroll edge to edge with its inset (and an edge control's focus ring) inside its clip.
        className="w-[560px] gap-sm rounded-l-lg px-0 py-xl sm:max-w-none"
        showCloseButton={!isSaving}
        // The backdrop never dismisses. Radix routes an overlay press through
        // `onPointerDownOutside` and a focus escape through `onInteractOutside`,
        // so both are cancelled; `onEscapeKeyDown` is left alone, because Escape
        // is the deliberate exit the original kept too.
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (isSaving) event.preventDefault();
        }}
      >
        <ManageToolWorkspacesForm
          key={tool.id}
          tool={tool}
          allWorkspaces={allWorkspaces}
          onSave={onSave}
          onClose={handleClose}
          isSaving={isSaving}
          isError={isError}
        />
      </SheetContent>
    </Sheet>
  );
}

interface ManageToolWorkspacesFormProps {
  tool: ToolRowData;
  allWorkspaces: WorkspaceResponseDto[];
  onSave: ManageToolWorkspacesSheetProps['onSave'];
  onClose: () => void;
  isSaving: boolean;
  isError: boolean;
}

function ManageToolWorkspacesForm(props: ManageToolWorkspacesFormProps) {
  const { tool, allWorkspaces, onSave, onClose, isSaving, isError } = props;
  const { t } = useTranslation(['admin', 'common']);

  const isNoWorkspacesEnabled = useBooleanFlagValue('no_workspaces_agent_access', false);

  const [accessType, setAccessType] = useState<WorkspaceAccess>(() =>
    tool.workspaceAccess === 'none' && !isNoWorkspacesEnabled ? 'all' : tool.workspaceAccess,
  );
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<WorkspaceResponseDto[]>(
    tool.workspaces,
  );

  const isScoped = accessType === 'only' || accessType === 'except';
  const canSave = !isScoped || selectedWorkspaces.length > 0;

  const availableWorkspaces = allWorkspaces.filter(
    (workspace) => !selectedWorkspaces.some((selected) => selected.id === workspace.id),
  );

  const handleSave = async () => {
    try {
      await onSave({
        agentId: tool.id,
        newAccess: accessType,
        newWorkspaces: selectedWorkspaces,
      });
      onClose();
    } catch {
      // The host owns the mutation and reports the failure back through `isError`.
      // Catching here is what stops the rejection escaping unhandled, which is how
      // the original lost the error entirely.
    }
  };

  return (
    <>
      <SheetHeader className="p-0 px-xl">
        <SheetTitle>{t('admin.manageToolWorkspacesModal.manageToolWorkspaces')}</SheetTitle>
      </SheetHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-lg overflow-y-auto px-xl py-2xs">
        <p className="flex items-center gap-sm text-body-lg text-muted-foreground">
          <ToolBadge toolId={tool.id} category={tool.tag} shape="circle" size="sm" />
          {t('admin.manageToolWorkspacesModal.managingWorkspacesForTool', { toolName: tool.name })}
        </p>

        <hr className="border-border" />

        <section className="flex flex-col gap-md">
          <header className="flex items-start gap-sm">
            <LayoutGridIcon className="mt-3xs size-4 shrink-0 text-muted-foreground" aria-hidden />
            <div className="flex flex-col gap-3xs">
              <h3 className="type-body-md-strong text-foreground">
                {t('admin.manageToolWorkspacesModal.workspaces')}
              </h3>
              <p className="text-body-sm text-muted-foreground">
                {t('admin.manageToolWorkspacesModal.selectWhichWorkspacesThe', {
                  toolName: tool.name,
                })}
              </p>
            </div>
          </header>

          <RadioGroup
            className="gap-md"
            value={accessType}
            onValueChange={(next) => setAccessType(next as WorkspaceAccess)}
            disabled={isSaving}
            aria-label={t('admin.manageToolWorkspacesModal.workspaces')}
          >
            <AccessOption value="all" title={t('admin.manageToolWorkspacesModal.allWorkspaces')} />
            <AccessOption
              value="only"
              title={t('admin.manageToolWorkspacesModal.onlyInSelectedWorkspaces', {
                selectedCount: selectedWorkspaces.length,
              })}
              description={t('admin.manageToolWorkspacesModal.thisToolWillBeAvailable')}
            />
            <AccessOption
              value="except"
              title={t('admin.manageToolWorkspacesModal.allExceptSelectedWorkspaces', {
                selectedCount: selectedWorkspaces.length,
              })}
              description={t('admin.manageToolWorkspacesModal.thisToolWillBeAvailable2')}
            />
            {isNoWorkspacesEnabled && (
              <AccessOption
                value="none"
                title={t('admin.manageToolWorkspacesModal.noWorkspaces')}
                description={t('admin.manageToolWorkspacesModal.thisToolWillNotBe')}
              />
            )}
          </RadioGroup>

          {isScoped && (
            <TagInput<WorkspaceResponseDto>
              id={WORKSPACE_PICKER_ID}
              value={selectedWorkspaces}
              onChange={setSelectedWorkspaces}
              options={availableWorkspaces}
              disabled={isSaving}
              getOptionLabel={(workspace) => workspace.name}
              getOptionValue={(workspace) => workspace.id}
              isOptionEqualToValue={(a, b) => a.id === b.id}
            >
              <TagInput.Chips<WorkspaceResponseDto>
                placeholder={t('admin.manageToolWorkspacesModal.selectWorkspaces')}
                renderChip={(workspace, onRemove) => (
                  <span className="flex items-center gap-2xs rounded-full bg-accent px-xs py-3xs text-body-sm text-accent-foreground">
                    <WorkspaceAvatar workspace={workspace} />
                    {workspace.name}
                    <Button
                      variant="ghost"
                      size="icon-mini"
                      className="rounded-full"
                      disabled={isSaving}
                      aria-label={t('common:ui.tagInput.removeTag', { label: workspace.name })}
                      onClick={onRemove}
                    >
                      <XIcon aria-hidden />
                    </Button>
                  </span>
                )}
              />
              <TagInput.Content<WorkspaceResponseDto>
                renderOption={(workspace) => (
                  <span className="flex items-center gap-sm">
                    <WorkspaceAvatar workspace={workspace} />
                    {workspace.name}
                  </span>
                )}
              />
            </TagInput>
          )}

          {isError && (
            <Alert
              role="alert"
              variant="destructive"
              icon={<TriangleAlertIcon aria-hidden />}
              className="border-destructive"
            >
              <AlertDescription>
                {t('admin.manageToolWorkspacesModal.couldnTSaveTheWorkspace')}
              </AlertDescription>
            </Alert>
          )}
        </section>
      </div>

      <SheetFooter className="flex-row items-center justify-end gap-sm p-0 px-xl">
        <Button variant="outline" size="lg" disabled={isSaving} onClick={onClose}>
          {t('admin.manageToolWorkspacesModal.cancel')}
        </Button>
        <Button
          size="lg"
          disabled={!canSave || isSaving}
          aria-busy={isSaving}
          onClick={() => void handleSave()}
        >
          {isSaving && <Spinner aria-hidden />}
          {t('admin.manageToolWorkspacesModal.saveChanges')}
        </Button>
      </SheetFooter>
    </>
  );
}

interface AccessOptionProps {
  value: WorkspaceAccess;
  title: string;
  description?: string;
}

function AccessOption(props: AccessOptionProps) {
  const optionId = `${ACCESS_FIELD_ID}-${props.value}`;

  return (
    <div className="flex items-start gap-sm">
      <RadioGroupItem value={props.value} id={optionId} className="mt-3xs" />
      <Label htmlFor={optionId} className="flex flex-col items-start gap-3xs">
        <span className="type-body-md-strong text-foreground">{props.title}</span>
        {props.description && (
          <span className="text-body-sm font-normal text-muted-foreground">
            {props.description}
          </span>
        )}
      </Label>
    </div>
  );
}

function WorkspaceAvatar(props: { workspace: WorkspaceResponseDto }) {
  return (
    <Avatar size="xs" aria-hidden>
      <AvatarImage src={props.workspace.logoUrl ?? undefined} alt="" />
      <AvatarFallback>{props.workspace.name.charAt(0)}</AvatarFallback>
    </Avatar>
  );
}
